import { allRangeLabel, allRangeValue } from "../../util/allRange";
import React from "react";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Calendar } from "react-date-range";

import OutsideAlerter from "../utilities/useOutSideComponentClick";

import ROUTES from "../../constants/routes";
import { settingsPath } from "../../constants/routes";

import { getEntitlements } from "../../util/entitlements";

import { rgbToHex } from "../../util/tooltipService";
import {
  DateRangeFilterSet,
  availableFilters,
  getListItemIconUrl,
  handleFilterChange,
  historyDataDateRange,
  selectedDateRange,
  selectedFilter,
  setInitialDateFilter,
  useIcons,
  notifySidebarItemsUpdated,
} from "../../util/clusterService";

import { eventEmitter } from "../../eventEmitters/EventEmitters";
import { mode as playbackModeValue } from "../../util/playbackModeService";
import { formatUnderscoredLabel, isNoDataValue, noDataTooltip, readableLabel, VISITOR_CATEGORY_TOOLTIPS } from "../../util/helper";
import { Tooltip } from "react-tooltip";
import { createPortal } from "react-dom";
// Not playback-specific despite the module name - this just returns "which
// days had any session activity" for the site, the same question this
// picker's own calendar needs answered. Reusing PlaybackControlBar's
// already-working query rather than duplicating it.
import { getPlaybackDaysWithData, getPlaybackDayMetrics, playbackEmptyStateKeys } from "../../util/playbackModal";
import { historical } from "../../util/metricsModal";

import {
  initialHistoryDataDateRange,
  INITIAL_FILTERS,
} from "../../../src/constants/commonConstants";
import ActionType from "../../resources/enums";

import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import "./Sidebar.scss";

const Sidebar = () => {
  const { t: translation } = useTranslation();
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const {
    earliestAccessibleDate,
    retentionUpgradeAvailable,
  } = getEntitlements(authReducer?.accountRecord);
  // Off-canvas state for the phone layout only. Above $bp-phone the toggle
  // and the scrim are display:none, so this is inert there.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dateRangeType, setDateRangeType] = useState(
    selectedDateRange
      ? selectedDateRange
      : authReducer?.historicalFilterSelected.name
      ? authReducer?.historicalFilterSelected.name
      : historyDataDateRange[0]
  );
  const dispatch = useDispatch();
  // Icon mode is switched from the display-settings menu at the top of the dashboard now
  // (DisplaySettingsMenu); this list only follows it.
  const [iconsEnabled, setIconsEnabled] = useState(useIcons);
  useEffect(() => {
    const sync = () => setIconsEnabled(useIcons);
    eventEmitter.addListener("displaySettingsChanged", sync);
    return () => {
      eventEmitter.removeListener("displaySettingsChanged", sync);
    };
  }, []);
  // Playback feature: the Historical section's date-range picker controls a
  // live-mode-only concern (filtering the real-time bubble view's query) -
  // hidden during playback since playback already drives the same
  // visualization from its own picked date, and showing both would imply two
  // independent, conflicting date contexts.
  const [isPlaybackMode, setIsPlaybackMode] = useState(
    playbackModeValue === "playback"
  );
  // Whether the currently picked playback DAY has any sessions at all (from
  // getPlaybackDayMetrics' own summary.totalSession) - distinct from
  // activeUserDetails' currentUsers sum below, which is "who's onsite RIGHT
  // NOW at this point in the timeline" and legitimately hits 0 during any
  // lull in a normal day's traffic. The empty-state block should only show
  // for a day with genuinely zero data, not every quiet moment the playhead
  // passes through - mirrors AnalyticsView.tsx's own playbackDayHasSessions.
  const [playbackDayHasSessions, setPlaybackDayHasSessions] = useState(true);
  // WHY the day is empty, when it is empty because it was REFUSED rather than
  // because nothing happened. See playbackEmptyStateKeys.
  const [playbackRestrictedReason, setPlaybackRestrictedReason] = useState<string | null>(null);
  useEffect(() => {
    const onModeChanged = ({ mode, playbackDate }: { mode: string; playbackDate?: string | null }) => {
      setIsPlaybackMode(mode === "playback");
      if (mode === "playback" && playbackDate) {
        // tslint:disable-next-line: no-floating-promises
        getPlaybackDayMetrics(playbackDate).then((metrics) => {
          setPlaybackDayHasSessions((metrics?.summary?.totalSession || 0) > 0);
          setPlaybackRestrictedReason(
            metrics?.restricted ? (metrics as any)?.restrictedReason ?? "retention" : null
          );
        });
      }
    };
    eventEmitter.addListener("modeChanged", onModeChanged);
    return () => {
      eventEmitter.removeListener("modeChanged", onModeChanged);
    };
  }, []);
  // Live-mode counterpart to playbackDayHasSessions above: activeUserDetails'
  // currentUsers sum is "who's onsite RIGHT NOW", which legitimately hits 0
  // between visitors even on an otherwise active day - that's not the same
  // as "no sessions happened today" (reported directly: the empty state kept
  // showing after every visitor onsite had already departed, even though the
  // day clearly had sessions per the Footer's own totals). historical.total
  // Session is clusterService's already-live-maintained count of TODAY's
  // departed sessions (see AnalyticsView.tsx's own refreshHistoricalEmptyState/
  // historicalSessionsChanged - this listens to the exact same signal), so
  // combined with the onsite sum it gives the day's true running total.
  const [dayHasHistoricalSessions, setDayHasHistoricalSessions] = useState(
    historical.totalSession > 0
  );
  useEffect(() => {
    const onHistoricalSessionsChanged = ({ hasSessions }: { hasSessions: boolean }) => {
      setDayHasHistoricalSessions(hasSessions);
    };
    eventEmitter.addListener("historicalSessionsChanged", onHistoricalSessionsChanged);
    return () => {
      eventEmitter.removeListener("historicalSessionsChanged", onHistoricalSessionsChanged);
    };
  }, []);
  useEffect(() => {
    getMetricsDataInfo();
  }, []);
  useEffect(() => {
    setShowAvailableFilters(availableFilters);
  }, [availableFilters]);
  useEffect(() => {
    // Was registered directly in the component body (no useEffect), so a
    // fresh listener was added on literally every render with no cleanup -
    // confirmed directly via a MaxListenersExceededWarning (31 and
    // climbing) after a few minutes of live traffic. Each render's Sidebar
    // re-mount left its own listener behind, so "userCountChanged" firing
    // once ended up running notifySidebarItemsUpdated() 31+ times
    // synchronously instead of once - a real, worsening-over-time cause of
    // the intermittent main-thread stutter reported during bubble
    // movement, not just a leak in the abstract.
    const onUserCountChanged = () => {
      // Route through the same freeze-aware notifier the rest of the sidebar
      // list refreshes use (see notifySidebarItemsUpdated in clusterService) -
      // this listener fires on literally every arrival/departure, and calling
      // setActiveUserDetails directly here bypassed that freeze entirely,
      // which is what let the list resort out from under an in-progress gooey
      // burst even after the freeze was added elsewhere.
      notifySidebarItemsUpdated();
    };
    eventEmitter.addListener("userCountChanged", onUserCountChanged);
    return () => {
      eventEmitter.removeListener("userCountChanged", onUserCountChanged);
    };
  }, []);
  const siteId = authReducer.authData.site._id;
  const [menuType, setMenuType] = useState<any>(
    selectedFilter ? selectedFilter : INITIAL_FILTERS[0]
  );
  const [changeCustomRange, setChangeCustomRange] = useState<boolean>(false);

  const [dropClass, setDropClass] = useState(false);
  const [showAvailableFilters, setShowAvailableFilters] =
    useState(availableFilters);

  const [isSelected, setIsSelected] = useState({
    selected: true,
    name:
      selectedDateRange && selectedDateRange.name
        ? selectedDateRange.name
        : authReducer?.historicalFilterSelected.name
        ? authReducer?.historicalFilterSelected.name
        : historyDataDateRange[0].name,
  });
  const [selectedHistoricalFilter, setSelectedHistoricalFilter] = useState(
    historyDataDateRange &&
      historyDataDateRange[0] &&
      historyDataDateRange[0].value
      ? historyDataDateRange[0]
      : initialHistoryDataDateRange[0]
  );

  const dateRangeRef = useRef<any>(null);

  const handleHistoricalFilterSelect = (selectedFilter, e) => {
   
    
    e.stopPropagation();
    setSelectedHistoricalFilter(selectedFilter);
    setIsSelected({
      selected: true,
      name: selectedFilter.name,
    });
    handleApplyFilter(selectedFilter);
   
  };
  const handleApplyFilter = (filters?: { name: string; value: number }) => {
   
    const selectedFilters = filters ? filters : selectedHistoricalFilter;
    setDropClass(false);
    setChangeCustomRange(false);
    if (selectedFilters) {
      DateRangeFilterSet(
        selectedFilters.name === "All"
          ? { name: "All", value: allRangeValue(authReducer.authData?.createdAt) }
          : {
              name: selectedFilters.name,
              value: selectedFilters.value,
            }
          ? {
              name: selectedFilters.name,
              value: new Date(selectedFilters.value).toISOString(),
            }
          : { name: "Today", value: new Date().toISOString() },
        // Real-time page: the end of any window is always "now", never a
        // picked-in-the-past date (see the Calendar's own comment above) -
        // this used to read dateRange[0].endDate, which could be an
        // arbitrary past date the two-endpoint DateRange picker allowed
        // selecting before it was replaced with a single-date Calendar.
        { name: "Custom Date", value: new Date().toISOString() }
      );
      setDateRangeType(selectedFilters);
      dispatch({
        type: ActionType.HISTORICAL_FILTER_SELECTED,
        payload: selectedFilters,
      });
    }
   
  };

  const handleCustomRangeClick = (selectedFilter, e) => {
    e.stopPropagation();
    setDateRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
    // The calendar now appears as its own overlay on top of the selector
    // (see the JSX below), not alongside the preset list inside the same
    // dropdown - so opening it closes the list instead of widening
    // alongside it.
    setDropClass(false);
    setChangeCustomRange(true);
  };

  const getMetricsDataInfo = async () => {
    eventEmitter.emit("realtimeFilterChanged");
  };
  const [activeUserDetails, setActiveUserDetails] = useState([]);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: null,
      key: "selection",
    },
  ]);

  // "Which days have any data" hint for the Custom Date calendar - same
  // treatment as PlaybackControlBar's date picker, reused here so the two
  // date pickers in the app read as one consistent design language rather
  // than two different ones.
  const [daysWithData, setDaysWithData] = useState<Set<string>>(new Set());
  const toIsoDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  const fetchDaysWithDataForMonth = (monthDate: Date) => {
    const rangeStart = new Date(
      monthDate.getFullYear(),
      monthDate.getMonth(),
      1
    );
    const rangeEnd = new Date(
      monthDate.getFullYear(),
      monthDate.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );
    getPlaybackDaysWithData(rangeStart, rangeEnd)
      .then((days) => setDaysWithData(new Set(days)))
      .catch((err) =>
        console.error("getPlaybackDaysWithData failed", err)
      );
  };
  useEffect(() => {
    if (changeCustomRange) {
      fetchDaysWithDataForMonth(dateRange[0].startDate || new Date());
    }
  }, [changeCustomRange]);

  const events = () => {
    // Use the emitted payload, not a fresh read of the live `items` binding.
    // clusterService.ts computes that payload with actively-bursting rows
    // pinned to their last-rendered position (see notifySidebarItemsUpdated's
    // comment) specifically so a resort can't repaint a row out from under
    // its own still-playing burst - re-reading `items` directly here bypassed
    // that pinning entirely and always rendered the current, unpinned sort
    // order regardless of what was emitted.
    eventEmitter.addListener("sideBarRealtimeItemsUpdated", (data) => {
      setActiveUserDetails(Array.isArray(data) ? data : []);
    });
  };

  useEffect(() => {
    if (dateRangeRef.current) {
      const { current: dateRangePicker } = dateRangeRef;

      dateRangePicker.addEventListener("change", (event: any) => {
        const { startDate, endDate } = event.target.value;
      });
    }
  }, []);

  useEffect(() => {
    events();
    fetchData();
  }, []);

  const fetchData = async () => {
    setInitialDateFilter(authReducer?.accountRecord);
  };
  const someThing =
    window.innerHeight * 0.85 > 500 ? window.innerHeight * 0.85 : 500;

  const handleOutsideClick = () => {
    setDropClass(false);
    setChangeCustomRange(false);
  };

  const mainClusterHeight = someThing * 0.75 + 130;
  return (
    <>
      {/*
        PHONE: the sidebar becomes an overlay rather than a column.
        At 375px it was 170px wide - 45% of the screen - which left the grid
        container just 185px and forced the stacked layout back down to 0.53x,
        i.e. the same unreadability the stacking had just fixed, arriving by a
        different route. Off-canvas, the grid gets ~355px instead (~0.9x).

        The toggle is rendered unconditionally and hidden by CSS above the
        breakpoint, so there is no JS width check to drift out of sync with
        $bp-phone - the media query is the single source of truth. (The one
        $bp-narrow no longer has a JS counterpart at all - the grid derives its
        column count from measured width - so the media query is the only
        definition of the narrow layout.)
      */}
      <button
        type="button"
        className="sidebar-overlay-toggle"
        aria-label={sidebarOpen ? "Hide visitor list" : "Show visitor list"}
        aria-expanded={sidebarOpen}
        onClick={() => setSidebarOpen((open) => !open)}
      >
        {sidebarOpen ? "\u00d7" : "\u2630"}
      </button>
      {/*
        Tapping the dimmed area closes it - the expected way out of an
        off-canvas panel, and it means the close control is never the only
        way back to the view.
      */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay-scrim"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <div
        className={`sidebar-main${sidebarOpen ? " sidebar-open" : ""}`}
        style={{ minHeight: mainClusterHeight }}
      >
        <div>
          <div className="real-time">
            <div className="real-time-heading">
              <h5>{translation("real_time")}</h5>
            </div>
            <div className="dropdown">
              <button
                type="button"
                className="btn btn-primary dropdown-toggle"
                data-bs-toggle="dropdown"
                onClick={() => setDropClass(false)}
              >
                {<span>{translation("by")} </span>}
                {menuType.name}
              </button>
              <ul className="dropdown-menu">
                {showAvailableFilters &&
                  showAvailableFilters.map((ele: any) => (
                    <li
                      className={
                        ele.menuType === menuType?.menuType
                          ? "dropdown-item active"
                          : "dropdown-item"
                      }
                      onClick={() => {
                        handleFilterChange(ele);
                        setMenuType(ele);
                      }}
                    >
                      {ele.name}
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          {/* "All visitors"/every category still renders a row with
              currentUsers: 0 even when nobody's onsite (it's a permanent
              placeholder, not conditionally added) - activeUserDetails.length
              alone can't tell "no one's here" from "someone's here in a
              category we haven't hidden yet", so this sums the actual counts
              instead. */}
          {(activeUserDetails || []).reduce(
            (sum: number, ele: any) => sum + (ele.currentUsers || 0),
            0
          ) === 0 &&
          // "No one currently onsite" just means the playhead (in playback)
          // or the current moment (live) is between visitors on an otherwise
          // real day - that's normal and should keep showing the ordinary
          // (all-zero) list. Only treat it as the empty state when the WHOLE
          // day genuinely has zero data: playbackDayHasSessions for playback,
          // dayHasHistoricalSessions (today's already-departed session count)
          // for live - the earlier live version only checked the onsite sum,
          // so it kept showing "No sessions" for the rest of the day the
          // moment the last visitor of an otherwise active day left
          // (reported directly).
          (isPlaybackMode ? !playbackDayHasSessions : !dayHasHistoricalSessions) ? (
            <div className="sidebar-empty-state">
              <h4>
                {translation(
                  isPlaybackMode
                    ? playbackEmptyStateKeys(playbackRestrictedReason).title
                    : "no_sessions"
                )}
              </h4>
              <p>
                {translation(
                  isPlaybackMode
                    ? playbackEmptyStateKeys(playbackRestrictedReason).desc
                    : "you_will_see_sessions_here_as_soon_as"
                )}
              </p>
            </div>
          ) : (
          <ul className="filter-data">
            {activeUserDetails &&
              activeUserDetails?.map((ele: any, index: number) => {
                const iconUrl = iconsEnabled
                  ? getListItemIconUrl(menuType?.menuType, ele.name)
                  : undefined;

                // "/" means two different things depending on which filter
                // is active - the "Referral Link" filter's own "no real
                // referrer" bucket (mapper.ts/metrics.ts both default to
                // this literal value for direct traffic), vs. a page path
                // meaning the homepage everywhere else "/" shows up (page
                // boxes, the Pages tab). A muted qualifier disambiguates
                // which one this row means without inventing a whole new
                // category value on the backend.
                const mutedSuffix =
                  menuType?.menuType === "referrer" && ele.name === "/"
                    ? "Direct"
                    : undefined;

                // Visitor Type (bot/human/AI - 'search_engine_bot',
                // 'other_bot', ...) and New vs Returning ('new'/'return' -
                // see SessionApi/Service/session.ts) are both raw lowercase
                // enum strings never meant for display as-is - see
                // formatUnderscoredLabel's own comment for why this is
                // scoped to just these filters instead of applied to every
                // category label everywhere.
                const displayName = readableLabel(menuType?.menuType, ele.name);

                // Definition on hover, ONLY for Visitor Type - the one filter
                // whose values are jargon a customer has never met ("AI Agent"
                // vs "AI Crawler" means nothing without being told). Every
                // other filter's values are self-explanatory (a country, a
                // browser), so they get no tooltip and no cost.
                const categoryDefinition = isNoDataValue(ele.name)
                  ? noDataTooltip(menuType?.menuType)
                  : menuType?.menuType === "visitor_category"
                  ? VISITOR_CATEGORY_TOOLTIPS[ele.name]
                  : undefined;

                // A name too long for its column (a link) shows in full after a short pause, on the same shared tooltip as the definitions above
                const fullName = !categoryDefinition && displayName.length > 12 ? displayName : undefined;
                const tipContent = categoryDefinition ?? fullName;

                return (
                  <li
                    data-name={ele.name}
                    data-tooltip-id={tipContent ? "visitor-category-definition" : undefined}
                    data-tooltip-content={tipContent}
                    data-tooltip-delay-show={fullName ? 450 : undefined}
                  >
                    <p>
                      {displayName}
                      {mutedSuffix && (
                        <span className="muted-label-suffix"> {mutedSuffix}</span>
                      )}
                    </p>
                    <p>
                      {ele.currentUsers}
                      {iconUrl ? (
                        <img
                          className="flag-status"
                          src={iconUrl}
                          alt={ele.name}
                        />
                      ) : (
                        ele.colorInfo && (
                          <span
                            className="color-status"
                            style={{ backgroundColor: rgbToHex(ele.colorInfo) }}
                          />
                        )
                      )}
                    </p>
                  </li>
                );
              })}
          </ul>
          )}
          {/*
            ONE shared tooltip for the whole list, anchored by data-tooltip-id -
            deliberately NOT a <Tooltip> inside the .map() above. Mounting one
            per row is the exact mistake documented in CLAUDE.md's perf notes
            (a per-item tooltip in the Sessions tab produced 25 Long Tasks on
            two clicks), and this list can hold many rows.

            Portalled with positionStrategy="fixed" for the same reason
            SessionRow.tsx does it: the sidebar is a scrolling container, and an
            absolutely-positioned tooltip would be clipped by it.
          */}
          {createPortal(
            <Tooltip
              id="visitor-category-definition"
              positionStrategy="fixed"
              place="right"
              className="visitor-category-tooltip"
            />,
            document.body
          )}
        </div>

        {!isPlaybackMode && (
        <div className="historical">
          <h5>{translation("historical")}</h5>
          <OutsideAlerter onOutsideClick={handleOutsideClick}>
            <div className={changeCustomRange ? "dropdown custom-range-open" : "dropdown"}>
              <button
                type="button"
                className="btn btn-primary dropdown-toggle"
                onClick={() => setDropClass(!dropClass)}
              >
                {dateRangeType.name === "Custom Date"
                  ? `${new Date(selectedDateRange.value).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      }
                    )}

                  - Today`
                  : dateRangeType.name === "All"
                  ? allRangeLabel(authReducer.authData?.createdAt)
                  : dateRangeType.name
                  ? dateRangeType.name
                  : "Today"}
              </button>

              {dropClass && !changeCustomRange && (
                <div className="dropdown-menu historical-dropwown">
                  <ul>
                    {historyDataDateRange &&
                      historyDataDateRange.map((ele: any) => (
                        <li
                          className={
                            isSelected.name === ele.name && isSelected.selected
                              ? "dropdown-item active"
                              : "dropdown-item"
                          }
                          onClick={(e) => {
                            ele.name === "Custom Date"
                              ? (setIsSelected({
                                  selected: true,
                                  name: ele.name,
                                }),
                                handleCustomRangeClick(ele, e))
                              : (setIsSelected({
                                  selected: true,
                                  name: ele.name,
                                }),
                                handleHistoricalFilterSelect(ele, e));
                          }}
                        >
                          {ele.name}
                        </li>
                      ))}
                  </ul>
                  {earliestAccessibleDate && retentionUpgradeAvailable && (
                    <p className="storage-limit-note">
                      {translation("data_older_than")}{" "}
                      {earliestAccessibleDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}{" "}
                      {translation("isnt_available_on_your_plan")}{" "}
                      <Link
                        to={settingsPath()}
                        className="upgrade-action"
                      >
                        {translation("upgrade_now")}
                      </Link>
                    </p>
                  )}
                </div>
              )}

              {changeCustomRange && (
                <div className="custom-range-panel">
                  {/* Real-time page: a session open right now is part of
                      TODAY and will close at some later point today - a
                      window whose end is pinned to a past date can never
                      correctly represent that closure, since it happens
                      chronologically after the window already ended. Every
                      preset (Today/Last 7 Days/etc.) already only lets the
                      START vary while the end is implicitly "now" - this
                      single-date Calendar (not the two-endpoint DateRange
                      it replaced) enforces the same rule for "Custom Date":
                      you pick how far back to start, the end is always
                      today. Picking a date applies and closes immediately
                      (mirrors PlaybackControlBar's date picker) - there's
                      no separate Apply/Cancel step anymore. */}
                  <Calendar
                    date={dateRange[0].startDate}
                    onChange={(selectedDate) => {
                      setDateRange([
                        {
                          startDate: selectedDate,
                          endDate: new Date(),
                          key: "selection",
                        },
                      ]);
                      setChangeCustomRange(false);
                      handleApplyFilter({
                        name: "Custom Date",
                        value: selectedDate,
                      });
                    }}
                    maxDate={new Date()}
                    minDate={earliestAccessibleDate ?? undefined}
                    onShownDateChange={fetchDaysWithDataForMonth}
                    dayContentRenderer={(day: Date) => (
                      <span
                        className={
                          daysWithData.has(toIsoDate(day))
                            ? "rdrDayHasData"
                            : ""
                        }
                      >
                        {day.getDate()}
                      </span>
                    )}
                  />
                </div>
              )}
            </div>
          </OutsideAlerter>
        </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
